import React, { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import S3 from 'react-aws-s3';
import { v4 as uuidv4 } from 'uuid';
import { FormField, DropZoneField, DropZoneFieldLoading, FormLabel } from '@app.shared/Style/Forms';
import { FieldValidationMessage } from '@app.shared/Forms';
import BarLoader from 'react-spinners/BarLoader';

export const FileField = ({ field, maxFiles = 1, label, dropZoneText, accept, dirName }) => {
  const [loading, setLoading] = useState(false);
  const { errors, values, touched, setFieldValue } = useFormikContext();

  const onDrop = useCallback((acceptedFiles) => {
    // console.log('Process files:', acceptedFiles);
    if (Boolean(acceptedFiles.length)) {
      acceptedFiles.forEach((file) => {
        uploadFile(file);
      });
    }
    // Do something with the files
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections, acceptedFiles } = useDropzone({
    onDrop,
    maxFiles,
    accept,
  });

  const acceptedFileItems = acceptedFiles?.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const fileRejectionItems = fileRejections?.map(({ file, errors }) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  const uploadFile = (file) => {
    setLoading(true);

    const s3config = {
      bucketName: process.env.AWS_BUCKET_NAME,
      dirName: dirName || process.env.AWS_DIRNAME,
      region: process.env.AWS_REGION,
      accessKeyId: process.env.AWS_KEY_ID,
      secretAccessKey: process.env.AWS_SECRET,
    };
    const s3Client = new S3(s3config);

    // Change file name before upload but keep the extension
    const fileName = `${file.name}`;
    const fileExtension = fileName.split('.').pop();
    const fileNameWithExtension = `${uuidv4()}.${fileExtension}`;

    s3Client.uploadFile(file, fileNameWithExtension).then((data) => {
      if (data.status === 204) {
        // console.log('Successfully uploaded file.', data);
        setFieldValue(field, data.location);
      } else {
        console.log('Fail', data);
      }
      setLoading(false);
    });
  };

  return (
    <FormField fullWidth>
      <FormLabel>
        <span>{label}</span>
      </FormLabel>
      <DropZoneField {...getRootProps()}>
        <input {...getInputProps()} />
        {loading ? (
          <DropZoneFieldLoading>
            <div className="message">Uploading... Please wait for completion.</div>
            <BarLoader></BarLoader>
          </DropZoneFieldLoading>
        ) : (
          <>{isDragActive ? <p>Drop the file here ...</p> : <p>{dropZoneText}</p>}</>
        )}
        {fileRejections.length > 0 && <ul>{fileRejectionItems}</ul>}
        {acceptedFiles.length > 0 && <ul>{acceptedFileItems}</ul>}
      </DropZoneField>
      <FieldValidationMessage errors={errors} touched={touched} name={field} />
      <input type="hidden" name="resume" value={values[field]} />
    </FormField>
  );
};
